import {
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useMatch, Link as RouterLink } from "react-router-dom";
import Page from "../Page";
import * as React from "react";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import {
  useGetDetail,
  useGetMeterValues,
  useMutate,
} from "src/hooks/useFetch.hook";
import { fDateTime, onlyTime, fTransactionYearFirstDateTime } from "src/utils/formatTime";
import { BackButton } from "src/common/components/BackButton";
import { useDomainConfig } from "src/hooks/useDomainConfig";
import { useAccess } from "src/hooks/useAccess.hook";
import sidebarConfig from "src/layouts/dashboard/SidebarConfig";
import enums, { FEATURE } from "src/util/enums";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { Loader } from "../Loader";
import {
  chartLabelConstants,
  chartTypeToShow,
} from "./TransactionsList/TransactionsList.constants";
import { BaseChart } from "../charts/BaseOptionChart";
import { is } from "src/util/is";
import { Form, FormikProvider, useFormik } from "formik";
import { transactionStatusValidationSchema } from "src/util/validationSchemas";
import TransactionStatus from "../../data/transactionStatus";
import { getTeamUrl, transactionIdUrl } from "src/react-query/endPoints";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import downIcon from "@iconify/icons-eva/arrow-down-fill";
import upIcon from "@iconify/icons-eva/arrow-up-fill";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { domainDetails } from "src/utils/domainDetails";


const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "start",
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius * 0.8,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: `0 1px 3px ${theme.palette.grey[200]}20`,
  // Transitions removed
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    // Hover effects removed
  },
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadius * 0.8}px ${theme.shape.borderRadius * 0.8}px 0 0`,
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  width: "280px",
  height: "48px",
  fontSize: theme.spacing(2.2),
  fontWeight: 700,
  borderRadius: theme.shape.borderRadius * 2,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  boxShadow: `0 4px 14px ${theme.palette.primary.main}40`,
  '&:hover': {
    // Hover effects removed
  },
  // Transitions removed
}))

export const GridLabel = ({ label, value, color, xs, sm, md, children }) => (
  <Grid item xs={xs} sm={sm} md={md} sx={{ display: 'flex' }}>
    <Item sx={{ width: '100%', height: '100%' }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "80px", // Reduced height for more compact layout
          minHeight: "80px",
        }}
      >
        <h4 style={{ 
          margin: "0 0 4px 0", 
          fontSize: "0.75rem", 
          fontWeight: 600, 
          color: "#666",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          lineHeight: "1.1",
          flex: "0 0 auto"
        }}>
          {label}
        </h4>
        <div style={{ 
          flex: "1 1 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start"
        }}>
          {children ? (
            children
          ) : (
            <p style={{ 
              color: color ?? "#1976d2", 
              margin: 0,
              fontSize: "1.1rem",
              fontWeight: 700,
              wordBreak: "break-word",
              lineHeight: "1.3"
            }}>
              {value ?? "--"} 
            </p>
          )}
        </div>
      </div>
    </Item>
  </Grid>
);

const graphDataAccumulator = (dataArr) => {
  let accuObj = dataArr.reduce((accuObj, current) => {
    let val = is.empty(current.value) ? 0 : current.value;
    if (accuObj[current.measurand]) {
      accuObj[current.measurand] = {
        ...accuObj[current.measurand],
        xaxis: [
          ...accuObj[current.measurand]["xaxis"],
          new Date(current.value_timestamp).getTime(),
        ],
        yaxis: [...accuObj[current.measurand]["yaxis"], val],
      };
    } else {
      let initialTime = new Date(current.value_timestamp);
      initialTime.setMinutes(initialTime.getMinutes() - 5);

      accuObj[current.measurand] = {
        xaxis:
          current.measurand !== "Temperature"
            ? [new Date(current.value_timestamp).getTime()]
            : [
              initialTime.getTime(),
              new Date(current.value_timestamp).getTime(),
            ],
        yaxis: current.measurand !== "Temperature" ? [val] : [0, val],
        unit: current.unit,
        label: chartLabelConstants[current.measurand],
        measurand: current.measurand,
      };
    }
    return accuObj;
  }, {});
  const possibleMeasurands = Object.keys(accuObj);
  accuObj = { ...accuObj, possibleMeasurands };
  return accuObj;
};

export const TransactionView = () => {
  const { id } = useParams();
  const isRefundDetail = useMatch("/refund/:id");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { teamId } = useSelectedTeam();
  const hasViewAccess = useAccess(FEATURE.TRANSACTION, "V");
  const hasReadOnlyAccess = useAccess(FEATURE.TRANSACTION, "R");
  const hasAllAccess = useAccess(FEATURE.TRANSACTION, "FA");
  const { mutate, isLoading } = useMutate("transactionStatus", { id });
  const domainConfig = useDomainConfig();

  const [initialAmountLabel, setInitialAmountLabel] = React.useState("");
  const [remainingAmountLabel, setRemainingAmountLabel] = React.useState("");
  const [showMeterValues, setShowMeterValues] = React.useState(false);
  const [chartData, setChartData] = React.useState([]);

  // Invoice Generation Function (HTML to PDF)
  const generateInvoicePDF = () => {
    if (!transaction) return;

    const startTime = fDateTime(transaction.starttime, "yyyy-MM-dd HH:mm");
    const stopTime = transaction.stoptime ? fDateTime(transaction.stoptime, "yyyy-MM-dd HH:mm") : 'Ongoing';
    const startValue = parseFloat(transaction.start_meter_value || 0);
    const stopValue = parseFloat(transaction.stop_meter_value || 0);
    const currentValue = parseFloat(transaction.current_meter_value || 0);
    
    // Use stop meter value if available, otherwise use current meter value
    const endValue = stopValue > 0 ? stopValue : currentValue;
    const energyConsumed = endValue > startValue ? (endValue - startValue) / 1000 : 0;

    // Construct full logo URL
    const logoUrl = domainConfig.logoUrl.startsWith('http') 
      ? domainConfig.logoUrl 
      : `${window.location.origin}/${domainConfig.logoUrl}`;

    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${transaction.transactionId}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
          }
          .invoice-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
          .header {
            background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
            color: white;
            padding: 30px;
            margin: -40px -40px 30px -40px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .header-left {
            display: flex;
            align-items: center;
            gap: 20px;
          }
          .logo {
            width: 60px;
            height: 60px;
            background: white;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            border: 2px solid rgba(255,255,255,0.3);
            overflow: hidden;
          }
          .logo img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          .header h1 {
            margin: 0;
            font-size: 32px;
            font-weight: bold;
          }
          .company-info {
            text-align: right;
            font-size: 14px;
          }
          .invoice-details {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 8px;
          }
          .invoice-details h3 {
            margin: 0 0 10px 0;
            color: #1976d2;
          }
          .invoice-details p {
            margin: 5px 0;
            color: #666;
          }
          .customer-info, .station-info {
            flex: 1;
            margin-right: 20px;
          }
          .transaction-table {
            width: 100%;
            border-collapse: collapse;
            margin: 30px 0;
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .transaction-table th {
            background: #1976d2;
            color: white;
            padding: 15px;
            text-align: left;
            font-weight: bold;
          }
          .transaction-table td {
            padding: 15px;
            border-bottom: 1px solid #eee;
          }
          .transaction-table tr:last-child td {
            border-bottom: none;
          }
          .summary {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
          }
          .summary h3 {
            margin: 0 0 15px 0;
            color: #1976d2;
          }
          .summary p {
            margin: 8px 0;
            color: #666;
          }
          .total-amount {
            background: #e3f2fd;
            padding: 20px;
            border-radius: 8px;
            text-align: right;
            margin: 20px 0;
          }
          .total-amount h3 {
            margin: 0 0 10px 0;
            color: #1976d2;
          }
          .total-amount .amount {
            font-size: 24px;
            font-weight: bold;
            color: #1976d2;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 8px;
            color: #666;
          }
          @media print {
            body { background: white; }
            .invoice-container { box-shadow: none; margin: 0; padding: 0; }
            .header { margin: 0 0 30px 0; }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header">
            <div class="header-left">
              <div class="logo">
                <img src="${logoUrl}" alt="Company Logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                <div style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; font-size: 24px; font-weight: bold; color: #1976d2; background: white; border-radius: 8px;">A</div>
              </div>
              <h1>INVOICE</h1>
            </div>
            <div class="company-info">
              <div><strong>${domainConfig.title}</strong></div>
              <div>Email: ${domainConfig.email}</div>
            </div>
          </div>

          <div class="invoice-details">
            <div class="customer-info">
              <h3>Bill To:</h3>
              <p><strong>Email:</strong> ${transaction.email || 'N/A'}</p>
              <p><strong>Phone:</strong> ${transaction.phone || 'N/A'}</p>
            </div>
            <div class="station-info">
              <h3>Charging Station:</h3>
              <p><strong>Station:</strong> ${transaction.chargepoint_name || 'N/A'}</p>
              <p><strong>Station ID:</strong> ${transaction.chargepoint_id || 'N/A'}</p>
              <p><strong>Connector:</strong> ${transaction.connectorId || 'N/A'}</p>
            </div>
          </div>

          <div style="margin: 20px 0;">
            <p><strong>Invoice #:</strong> ${transaction.transactionId}</p>
            <p><strong>Date:</strong> ${fDateTime(transaction.starttime, "yyyy-MM-dd")}</p>
            <p><strong>Transaction ID:</strong> ${transaction.transactionId}</p>
          </div>

          <table class="transaction-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Start Time</th>
                <th>Stop Time</th>
                <th>Energy (kWh)</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>EV Charging Session</td>
                <td>${startTime}</td>
                <td>${stopTime}</td>
                <td>${energyConsumed.toFixed(2)}</td>
                <td>₹${(Number(transaction.totalAmountCharged) || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="4" style="text-align: right; font-weight: bold;">Charging Cost:</td>
                <td>₹${((Number(transaction.totalAmountCharged) || 0) / 1.18).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="4" style="text-align: right; font-weight: bold;">GST (18%):</td>
                <td>₹${(((Number(transaction.totalAmountCharged) || 0) / 1.18) * 0.18).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="4" style="text-align: right; font-weight: bold;">Total (Incl. GST):</td>
                <td>₹${(Number(transaction.totalAmountCharged) || 0).toFixed(2)}</td>
              </tr>
              ${transaction.totalRefund && transaction.totalRefund > 0 ? `
              <tr style="color: #d32f2f;">
                <td colspan="4" style="text-align: right; font-weight: bold;">
                  ${transaction.payment_type && (transaction.payment_type.toLowerCase().includes('wallet') || transaction.payment_type.toLowerCase().includes('balance')) ? 'Return to Wallet:' : 'Refund:'}
                </td>
                <td style="color: #d32f2f;">-₹${(Number(transaction.totalRefund) || 0).toFixed(2)}</td>
              </tr>
              ` : ''}
              <tr style="background-color: #f8f9fa;">
                <td colspan="4" style="text-align: right; font-weight: bold;">
                  ${transaction.payment_type && (transaction.payment_type.toLowerCase().includes('wallet') || transaction.payment_type.toLowerCase().includes('balance')) ? 'Initial Wallet Balance:' : 'Total Paid Amount:'}
                </td>
                <td style="font-weight: bold;">₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div class="summary">
            <h3>Summary</h3>
            <p><strong>Start Meter Reading:</strong> ${transaction.start_meter_value || 'N/A'} Wh</p>
            <p><strong>Stop Meter Reading:</strong> ${transaction.stop_meter_value || 'N/A'} Wh</p>
            <p><strong>Payment Method:</strong> ${transaction?.paymentDescription === "QRv2 Payment" || transaction?.paymentDescription?.toUpperCase().includes("UPI") ? "UPI" : transaction.payment_type || "N/A"}</p>
            <p><strong>Stop Reason:</strong> ${transaction.stop_reason || 'N/A'}</p>
            ${transaction.totalRefund && transaction.totalRefund > 0 ? `
            <p><strong>Refund Status:</strong> ${transaction.refundStatus || 'Processed'}</p>
            ` : ''}
          </div>

          <div class="summary">
            <h3>Amount Breakdown</h3>
            ${transaction.payment_type && (transaction.payment_type.toLowerCase().includes('wallet') || transaction.payment_type.toLowerCase().includes('balance')) ? `
            <p><strong>Initial Wallet Balance:</strong> ₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</p>
            ` : `
            <p><strong>Amount Paid:</strong> ₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</p>
            `}
            <p><strong>Charging Cost:</strong> ₹${((Number(transaction.totalAmountCharged) || 0) / 1.18).toFixed(2)}</p>
            <p><strong>GST (18%):</strong> ₹${(((Number(transaction.totalAmountCharged) || 0) / 1.18) * 0.18).toFixed(2)}</p>
            <p><strong>Total (Incl. GST):</strong> ₹${(Number(transaction.totalAmountCharged) || 0).toFixed(2)}</p>
            ${transaction.totalRefund && transaction.totalRefund > 0 ? `
            <p><strong>${transaction.payment_type && (transaction.payment_type.toLowerCase().includes('wallet') || transaction.payment_type.toLowerCase().includes('balance')) ? 'Return to Wallet:' : 'Refund Amount:'}</strong> -₹${(Number(transaction.totalRefund) || 0).toFixed(2)}</p>
            ` : ''}
          </div>

          <div class="summary">
            <h3>Payment Information</h3>
            <p><strong>Payment Method:</strong> ${transaction?.paymentDescription === "QRv2 Payment" || transaction?.paymentDescription?.toUpperCase().includes("UPI") ? "UPI" : transaction.payment_type || 'N/A'}</p>
            ${transaction.payment_type && (transaction.payment_type.toLowerCase().includes('wallet') || transaction.payment_type.toLowerCase().includes('balance')) ? `
            <p><strong>Initial Wallet Balance:</strong> ₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</p>
            <p><strong>Remaining Wallet Balance:</strong> ₹${((Number(transaction.totalAmountPaid) || 0) - (Number(transaction.totalAmountCharged) || 0)).toFixed(2)}</p>
            ` : `
            <p><strong>Total Paid Amount:</strong> ₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</p>
            `}
          </div>


          <div class="footer">
            <p><strong>Thank you for using ${domainConfig.name} Charging Services!</strong></p>
            <p>For support, contact: ${domainConfig.email}</p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Create a new window with the invoice HTML
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    
    // Wait for the content to load, then trigger print dialog
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const onSubmit = (transactionStatusObj, { setSubmitting }) => {
    const {
      actions: { setAlert },
    } = alertSlice;
    const { status } = transactionStatusObj;
    if (status === "None") {
      dispatch(
        setAlert({
          open: true,
          message: "Please select a status first",
          severity: enums.SEVERITY.ERROR,
        })
      );
      setSubmitting(false);
      return;
    }
    const mutateObject = {
      url: getTeamUrl(teamId, `${transactionIdUrl(id)}/status`),
      method: "PATCH",
    };

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: transactionStatusObj,
      },
      {
        onSuccess: () => {
          dispatch(
            setAlert({
              open: true,
              message: "Succesfully updated the transaction status",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
          // navigate(sidebarConfig[FEATURE.TRANSACTION].path)
        },
      }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: { notes: "", status: "" },
    validationSchema: transactionStatusValidationSchema,
    onSubmit: onSubmit,
  });

  const {
    errors,
    touched,
    values,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
  } = formik;

  React.useEffect(() => {
    // Only check authorization if we have user data loaded
    // This prevents false negatives when opening in new tabs
    if (teamId && hasViewAccess === false) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: "Not Authorized" })
      );
      navigate(sidebarConfig[FEATURE.TRANSACTION].path);
    }
  }, [hasViewAccess, navigate, dispatch, teamId]);

  const { data, isFetching } = useGetDetail(
    isRefundDetail ? "refund" : "transaction",
    {
      id,
    }
  );
  const [transaction] = data ?? [];
  const { data: meterValues, isFetching: fetchingMeterValues } =
    useGetMeterValues("transactionMeterValue", {
      showMeterValues,
      transactionId: transaction ? transaction?.transactionId : null,
      connectorId: transaction ? transaction?.connectorId : null,
    });

  React.useEffect(() => {
    if (!is.empty(transaction) && !isFetching) {
      const { status, notes, payment_type } = transaction;
      let initialAmtLabel = "";
      let remainingAmtLabel = "";
      switch (payment_type) {
        case "WALLET":
          initialAmtLabel = "Initial Wallet Balance";
          remainingAmtLabel = "Remaining Wallet Balance";
          break;
        case "CARD":
          initialAmtLabel = "Total Paid Amount";
          remainingAmtLabel = "Refund Amount";
          break;
        case "PREPAID":
          initialAmtLabel = "Initial Card Balance";
          remainingAmtLabel = "Remaining Card Balance";
          break;
        case "RFID":
        default:
          break;
      }

      const formValues = {
        status: status ? status : "None",
        notes: notes ? notes : "",
      };
      resetForm({ values: formValues });
      setRemainingAmountLabel(remainingAmtLabel);
      setInitialAmountLabel(initialAmtLabel);
    }
  }, [transaction, resetForm, isFetching]);

  React.useEffect(() => {
    if (showMeterValues && meterValues) {
      const chartValues = graphDataAccumulator(meterValues);
      const measurandsArr = chartValues.possibleMeasurands;
      const finalData = measurandsArr.map(
        (measurand) => chartValues[measurand]
      );
      setChartData(finalData);
    }
  }, [showMeterValues, meterValues]);

  // Show loader while authentication state is being determined
  if (isFetching || !teamId) {
    return <Loader />;
  }

  const viewLogs = () => {
    // Use our timezone utility function to handle conversion properly
    // This function handles both Date objects and string timestamps correctly
    const localStartTime = fTransactionYearFirstDateTime(transaction?.starttime);
    const localStopTime = fTransactionYearFirstDateTime(transaction?.stoptime);
    
    const logsData = {
      ...transaction,
      starttime: localStartTime,
      stoptime: localStopTime,
    };
    
    dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
      data: { dataForLogs: logsData },
      tabOpenedFor: "charger",
      tabOpened: "logs"
    }))
    navigate(`/charger/${transaction?.charge_box_pk}`)
  };

  return (
    <Page
      title={`${!isRefundDetail ? "Transactions" : "Refund"
        }`}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <BackButton
          text={`${!isRefundDetail ? "Transaction" : "Refund"} details`}
          path={`${!isRefundDetail ? "/transactions" : "/refund"}`}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            onClick={generateInvoicePDF}
            startIcon={<Icon icon="eva:file-text-outline" />}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
              boxShadow: '0 4px 14px 0 rgba(46, 125, 50, 0.39)',
              fontWeight: 700,
              letterSpacing: '0.5px',
              px: 2,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
                boxShadow: '0 6px 20px 0 rgba(46, 125, 50, 0.5)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            Generate Invoice
          </Button>
          <Button 
            variant="contained" 
            onClick={viewLogs}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
              fontWeight: 700,
              letterSpacing: '0.5px',
              px: 2,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                // Hover effects removed
              },
              '&:active': {
                // Active effects removed
              },
              // Transitions removed
            }}
          >
            View Logs
          </Button>
        </Box>
      </Box>
      <Card sx={{ 
        p: 2, 
        borderRadius: 2,
        background: `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
        boxShadow: `0 2px 12px rgba(0,0,0,0.06)`,
        border: `1px solid #e0e0e0`,
      }}>
        <Box sx={{ width: "100%" }}>
          <Grid
            container
            rowSpacing={0.5}
            columnSpacing={{ xs: 1, sm: 1.5, md: 2 }}
            alignItems="stretch"
          >
            <GridLabel
              label="Transaction ID"
              value={transaction?.transactionId}
              xs={12}
              sm={6}
              md={4}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="ChargerBox ID"
              value={transaction?.chargepoint_id}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label={"Chargepoint Name"}
              value={transaction?.chargepoint_name}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Connector ID"
              value={transaction?.connectorId}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Email"
              value={transaction?.email}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Phone Number"
              value={transaction?.phone ? transaction?.phone : "-"}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="OCPP ID Tag"
              value={transaction?.tag}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Start Time"
              value={fDateTime(transaction?.starttime, "yyyy-MM-dd hh:mm a")}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Stop Time"
              value={fDateTime(transaction?.stoptime, "yyyy-MM-dd hh:mm a")}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Start Meter Value"
              value={transaction?.start_meter_value}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Current Meter Value"
              value={transaction?.current_meter_value}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Stop Meter Value"
              value={transaction?.stop_meter_value}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="kWh Consumed"
              value={(() => {
                const startValue = parseFloat(transaction?.start_meter_value || 0);
                const stopValue = parseFloat(transaction?.stop_meter_value || 0);
                const currentValue = parseFloat(transaction?.current_meter_value || 0);
                
                // Use stop meter value if available, otherwise use current meter value
                const endValue = stopValue > 0 ? stopValue : currentValue;
                
                if (endValue > startValue) {
                  const kwConsumed = (endValue - startValue) / 1000;
                  return `${kwConsumed.toFixed(3)} kWh`;
                }
                return "0.000 kWh";
              })()}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Charging Cost"
              value={transaction?.totalAmountCharged}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label="Price"
              value={(() => {
                // Check for priceperkw (various naming conventions)
                const priceperkw = transaction?.priceperkw || transaction?.pricePerKw || transaction?.priceperKW || transaction?.price_per_kw;
                if (priceperkw && parseFloat(priceperkw) > 0) {
                  return `₹${parseFloat(priceperkw).toFixed(2)}/kWh`;
                }
                // Check for pricepermin (various naming conventions)
                const pricepermin = transaction?.pricepermin || transaction?.pricePerMin || transaction?.priceperMin || transaction?.price_per_min;
                if (pricepermin && parseFloat(pricepermin) > 0) {
                  return `₹${parseFloat(pricepermin).toFixed(2)}/min`;
                }
                return "--";
              })()}
            />
            {initialAmountLabel && (
              <GridLabel
                xs={12}
                sm={6}
                md={4}
                label={initialAmountLabel}
                value={transaction?.totalAmountPaid}
              />
            )}
            {remainingAmountLabel && (
              <GridLabel
                xs={12}
                sm={6}
                md={4}
                label={remainingAmountLabel}
                value={transaction?.totalRefund}
              />
            )}
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label={"Payment Type"}
              value={transaction?.paymentDescription === "QRv2 Payment" || transaction?.paymentDescription?.toUpperCase().includes("UPI") ? "UPI" : transaction?.payment_type} 
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label={"Payment ID"}
              value={
                transaction?.paymentId ? (
                  <Link
                    component={RouterLink}
                    to={`/refund-details/${transaction.paymentId}`}
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {transaction.paymentId}
                  </Link>
                ) : (
                  <span style={{ color: '#999' }}>—</span>
                )
              }
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label={"Stop Reason"}
              value={transaction?.stop_meter_value ? transaction?.stop_reason ? transaction?.stop_reason : "Remote" : "--"}
            />
            <GridLabel
              xs={12}
              sm={6}
              md={4}
              label={"Total Kw Consumed"}
              value={transaction?.stop_meter_value ? Math.abs(transaction?.stop_meter_value - transaction?.start_meter_value) / 1000 : "--"}
            />
            {isRefundDetail && (
              <GridLabel
                xs={12}
                sm={6}
                md={4}
                label="Refund Status"
                value={transaction?.refundStatus.toUpperCase()}
              />
            )}
          </Grid>

        </Box>

        <Box mt={2} p={1} sx={{ display: "flex", placeContent: "end", gap: "1rem" }}>
          <Button
            variant="contained"
            color={showMeterValues ? "error" : "primary"}
            onClick={() => setShowMeterValues(!showMeterValues)}
            endIcon={<Icon icon={showMeterValues ? upIcon : downIcon}></Icon>}
            sx={{
              background: showMeterValues 
                ? 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)'
                : 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
              boxShadow: showMeterValues 
                ? '0 4px 14px 0 rgba(211, 47, 47, 0.39)'
                : '0 4px 14px 0 rgba(46, 125, 50, 0.39)',
              fontWeight: 700,
              letterSpacing: '0.5px',
              px: 2,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                // Hover effects removed
              },
              '&:active': {
                // Active effects removed
              },
              // Transitions removed
            }}
          >
            {showMeterValues
              ? "Hide Charger Statistics"
              : "Show Charger Statistics"}
          </Button>
        </Box>
        {showMeterValues && chartData && chartData.length > 0 && (
          <Stack>
            <Grid container spacing={1}>
              {chartData.map(({ label, xaxis, yaxis, unit, measurand }) => {
                if (chartTypeToShow.includes(measurand)) {
                  let series = [
                    {
                      name: measurand,
                      data: yaxis,
                    },
                  ];
                  let options = {
                    chart: {
                      height: 200,
                      width: 300,
                      type: "line",
                      zoom: {
                        enabled: false,
                      },
                    },
                    dataLabels: {
                      enabled: false,
                    },
                    stroke: {
                      curve: "smooth",
                      width: 1,
                      lineCap: "butt",
                    },
                    title: {
                      text: label,
                      align: "left",
                    },
                    grid: {
                      row: {
                        colors: ["#C8FACD"],
                        opacity: 0.5,
                      },
                    },
                    xaxis: {
                      categories: xaxis,
                      type: "datetime",
                      timezone:
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                      labels: {
                        formatter: (value) => onlyTime(value),
                        rotate: 0,
                        maxWidth: 10,
                      },
                      title: {
                        text: "Time",
                        style: {
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "grey",
                          marginTop: "2px",
                        },
                      },
                    },
                    yaxis: {
                      title: {
                        text: `${unit}`,
                        style: {
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "grey",
                        },
                      },
                    },
                  };

                  return (
                    <Grid key={`chart-${label}`} item xs={12} md={6} sm={6} id={`chart-${label}`}>
                      <BaseChart
                        options={options}
                        series={series}
                        type="line"
                      />
                    </Grid>
                  );
                }
                return <div key={`empty-${label}`}></div>;
              })}
            </Grid>
          </Stack>
        )}

            <Divider textAlign="left" variant="fullWidth" sx={{ mb: 2, mt: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  px: 2,
                  py: 1,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  textAlign: 'left'
                }}
              >
                Transaction Status & Notes
              </Typography>
            </Divider>
        <FormikProvider value={formik}>
          <Form
            autoComplete="off"
            id={`transaction-status-form-${id}`}
            noValidate
            onSubmit={handleSubmit}
          >
            <Grid
              container
              rowSpacing={1}
              columnSpacing={{ xs: 1, sm: 1.5, md: 2 }}
              direction="row"
            >
              <Grid item xs={12} sm={12} md={12} >
                <FormControl
                  fullWidth
                  size="medium"
                  error={touched.status && errors.status}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    },
                  }}
                >
                  <InputLabel id={`transaction-status-${id}`} sx={{ fontWeight: 600 }}>
                    Transaction Status
                  </InputLabel>
                  <Select
                    labelId={`transaction-status-${id}`}
                    id={`transaction-status-${id}`}
                    disabled={
                      hasReadOnlyAccess ||
                      values.status === "CAPTURED" ||
                      values.status === "AUTHORIZED"

                    }
                    value={values.status}
                    onChange={(e) => setFieldValue("status", e.target.value)}
                    label="Transaction Status"
                    {...getFieldProps("status")}
                  >
                    <MenuItem selected value="None" disabled>
                      <em>None</em>
                    </MenuItem>
                    {TransactionStatus.map(
                      ({ label, value, disabled }, index) => {
                        return <MenuItem
                          key={`transaction-status-${index}-${label}`}
                          value={value}
                          disabled={disabled}
                        >
                          {label}
                        </MenuItem>
                      }
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={12} padding={1}>
                <FormControl
                  fullWidth
                >
                  <TextField
                    disabled={hasReadOnlyAccess}
                    multiline
                    sx={{ 
                      width: "100%",
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }}
                    rows={4}
                    id="transaction-notes"
                    label="Transaction Notes"
                    placeholder="Enter any additional notes about this transaction..."
                    {...getFieldProps("notes")}
                    error={Boolean(touched.notes && errors.notes)}
                    helperText={touched.notes && errors.notes}
                  />
                </FormControl>
              </Grid>

              {hasAllAccess &&
                <Grid item xs={12} sm={12} md={12} padding={1}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="left"
                  >
                    <LoadingButton
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={isSubmitting || isLoading}
                      sx={{
                        background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                        boxShadow: '0 4px 14px 0 rgba(46, 125, 50, 0.39)',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
                          boxShadow: '0 6px 20px 0 rgba(46, 125, 50, 0.5)',
                          transform: 'translateY(-2px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        '&.Mui-disabled': {
                          background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                          color: 'text.disabled',
                          boxShadow: 'none',
                        },
                        // Transitions removed
                      }}
                    >
                      {isSubmitting || isLoading ? 'Updating...' : 'Update Transaction'}
                    </LoadingButton>
                  </Stack>
                </Grid>
              }
            </Grid>

          </Form>
        </FormikProvider>
      </Card>
    </Page>
  );
};
